import { useState } from 'react';
import { useMutation, useQuery, gql } from '@apollo/client';

const IMPORT_CONTACT = gql`
  mutation ImportContact(
    $name: String!
    $phone: String!
    $email: String
    $groupName: String!
  ) {
    importContact(
      name: $name
      phone: $phone
      email: $email
      groupName: $groupName
    ) {
      id
      name
      phone
    }
  }
`;

const GET_GROUPS = gql`
  query GetGroups {
    contactGroups {
      id
      name
    }
  }
`;

function ImportContactsModal({ onClose, onSuccess }) {
  const [importType, setImportType] = useState('manual'); // 'manual' or 'excel'
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    groupName: '',
  });
  const [excelFile, setExcelFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { data: groupsData } = useQuery(GET_GROUPS);

  const [importContact] = useMutation(IMPORT_CONTACT, {
    onCompleted: () => {
      setSuccess('Entity imported successfully!');
      setFormData({ name: '', phone: '', email: '', groupName: '' });
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  const handleManualImport = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.name.trim()) {
      setError('Designation required');
      return;
    }

    if (!formData.phone.trim()) {
      setError('Network identifier required');
      return;
    }

    if (!formData.groupName.trim()) {
      setError('Collective assignment required');
      return;
    }

    const cleanPhone = formData.phone.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      setError('Invalid identifier length');
      return;
    }

    await importContact({
      variables: {
        name: formData.name,
        phone: cleanPhone,
        email: formData.email || null,
        groupName: formData.groupName,
      },
    });
  };

  const handleExcelImport = async (e) => {
    e.preventDefault();
    setError('');
    setImporting(true);

    setTimeout(() => {
      setImporting(false);
      setSuccess('Excel data matrix incoming shortly!');
    }, 1000);
  };

  return (
    <div className="fixed inset-0 bg-bulky-navy-900/60 backdrop-blur-md flex items-center justify-center z-[100] p-4 md:p-6">
      <div className="bg-white rounded-[2rem] md:rounded-[2.5rem] shadow-2xl max-w-2xl w-full overflow-hidden border border-white/20 animate-in fade-in zoom-in duration-300 max-h-[90vh] flex flex-col">
        <div className="bg-bulky-navy-800 p-6 md:p-10 text-white relative shrink-0">
          <div className="absolute top-0 right-0 w-32 h-32 bg-bulky-blue-500/10 rounded-bl-full animate-pulse"></div>
          <h2 className="font-medium">Matrix Ingestion</h2>
          <p className="text-bulky-navy-300 text-[9px] md:text-[10px] font-normal uppercase tracking-[0.2em] mt-2 relative z-10 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-bulky-blue-400"></span>
            Authorized batch identity import protocol
          </p>
          <button
            onClick={onClose}
            className="absolute top-6 right-6 md:top-8 md:right-8 w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 text-white/50 hover:text-white hover:bg-white/10 transition-all z-20"
          >
            <span className="material-icons-round text-xl">close</span>
          </button>
        </div>

        <div className="p-6 md:p-10 overflow-y-auto no-scrollbar">
          {/* Channel Selection */}
          <div className="flex gap-2 mb-8 bg-gray-50 p-1.5 rounded-2xl w-full sm:w-fit overflow-x-auto no-scrollbar">
            {[
              { id: 'manual', label: 'Single Entry', icon: 'edit' },
              { id: 'excel', label: 'Matrix Upload', icon: 'table_view' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setImportType(tab.id)}
                className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 sm:px-8 py-3.5 rounded-xl font-normal text-[9px] md:text-[10px] uppercase tracking-wider transition-all whitespace-nowrap ${importType === tab.id
                  ? 'bg-white shadow-xl text-bulky-blue-500'
                  : 'text-gray-400 hover:text-bulky-navy-800'
                  }`}
              >
                <span className="material-icons-round text-sm">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {(error || success) && (
            <div className={`mb-8 p-4 rounded-2xl flex items-center gap-4 text-[10px] font-normal uppercase tracking-wider animate-in fade-in slide-in-from-top-2 shadow-sm border-2 ${error ? 'bg-red-50 text-red-600 border-red-100' : 'bg-emerald-50 text-emerald-600 border-emerald-100'
              }`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${error ? 'bg-red-100' : 'bg-emerald-100'}`}>
                <span className="material-icons-round text-lg">{error ? 'error_outline' : 'verified'}</span>
              </div>
              <span>{error || success}</span>
            </div>
          )}

          {importType === 'manual' && (
            <form onSubmit={handleManualImport} className="space-y-8">
              <div className="bg-bulky-blue-50 rounded-2xl p-6 border border-bulky-blue-100/50">
                <div className="flex items-start gap-4">
                  <span className="material-icons-round text-bulky-blue-500 mt-0.5">info</span>
                  <p className="text-[10px] font-normal text-bulky-blue-600 uppercase tracking-wider leading-relaxed">
                    Manual protocol: Register identities individually. Existing terminal IDs will be updated and synchronized with the selected collective segment.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider mb-3 ml-1">Entity Designation</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-6 py-4.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-800 shadow-sm"
                    placeholder="Identity name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider mb-3 ml-1">Network Identifier</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-6 py-4.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-800 shadow-sm"
                    placeholder="919876543210"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider mb-3 ml-1">Protocol Address (Email)</label>
                <div className="relative group">
                  <span className="material-icons-round absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-bulky-blue-500 transition-colors">alternate_email</span>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full pl-14 pr-6 py-4.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-800 shadow-sm"
                    placeholder="entity@network.local"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider mb-3 ml-1">Collective Assignment</label>
                <div className="relative group">
                  <span className="material-icons-round absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-bulky-blue-500 transition-colors">hub</span>
                  <input
                    type="text"
                    list="groups"
                    value={formData.groupName}
                    onChange={(e) => setFormData({ ...formData, groupName: e.target.value })}
                    className="w-full pl-14 pr-6 py-4.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-800 shadow-sm"
                    placeholder="Designate collective segment"
                    required
                  />
                  <datalist id="groups">
                    {groupsData?.contactGroups.map(group => (
                      <option key={group.id} value={group.name} />
                    ))}
                  </datalist>
                </div>
                <p className="text-[9px] font-normal text-gray-400 uppercase tracking-wider mt-3 ml-1">New collectives will be auto-provisioned if not detected.</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-gray-100 mt-10">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full sm:flex-1 px-8 py-5 border-2 border-gray-100 text-bulky-navy-500 font-normal rounded-2xl hover:bg-gray-50 transition-all uppercase tracking-wider text-[10px]"
                >
                  Terminate
                </button>
                <button
                  type="submit"
                  className="w-full sm:flex-1 px-8 py-5 bg-bulky-blue-500 text-white font-normal rounded-2xl hover:bg-bulky-blue-600 shadow-xl shadow-bulky-blue-500/30 active:scale-95 transition-all text-[10px] uppercase tracking-[0.2em] flex items-center justify-center gap-3"
                >
                  <span className="material-icons-round text-lg">publish</span>
                  Authorize Import
                </button>
              </div>
            </form>
          )}

          {importType === 'excel' && (
            <form onSubmit={handleExcelImport} className="space-y-8">
              <div className="bg-bulky-navy-50 rounded-[2.5rem] p-6 md:p-10 border border-bulky-navy-100/50 relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-bulky-blue-500/5 rounded-full -mr-12 -mt-12 transition-transform duration-700 group-hover:scale-110"></div>
                <div className="flex items-center gap-4 mb-8 relative z-10">
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-bulky-blue-500 shadow-sm">
                    <span className="material-icons-round text-lg">playlist_add_check</span>
                  </div>
                  <h2 className="font-medium">Upload Protocol Sequence</h2>
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 relative z-10">
                  {[
                    'Upload Identity Matrix (XLSX/CSV)',
                    'Map Protocol Parameters',
                    'Review Integrity Check',
                    'Execute Final Sequence'
                  ].map((step, i) => (
                    <li key={i} className="flex items-center gap-4 text-[9px] md:text-[10px] font-normal text-bulky-navy-600 uppercase tracking-wider bg-white/60 backdrop-blur-sm p-4 rounded-2xl border border-white/50 shadow-sm hover:scale-[1.02] transition-transform">
                      <span className="w-6 h-6 rounded-lg bg-bulky-blue-500 text-white flex items-center justify-center text-[10px] shadow-sm">{i + 1}</span>
                      {step}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="relative group">
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => setExcelFile(e.target.files[0])}
                  className="hidden"
                  id="excel-upload"
                />
                <label
                  htmlFor="excel-upload"
                  className="cursor-pointer border-4 border-dashed border-gray-100 rounded-[2.5rem] md:rounded-[3rem] p-10 md:p-16 text-center hover:border-bulky-blue-500/30 hover:bg-bulky-blue-50/30 transition-all block group relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-bulky-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <div className="w-20 h-20 bg-gray-50 rounded-[2rem] flex items-center justify-center mx-auto mb-6 text-gray-300 group-hover:bg-white group-hover:text-bulky-blue-500 transition-all shadow-inner group-hover:shadow-xl group-hover:shadow-bulky-blue-500/10 group-hover:-translate-y-2 relative z-10">
                    <span className="material-icons-round text-5xl">{excelFile ? 'description' : 'cloud_upload'}</span>
                  </div>
                  <span className="block text-xs md:text-sm font-normal text-bulky-navy-800 uppercase tracking-wider mb-3 relative z-10">
                    {excelFile ? excelFile.name : 'Inject Data Matrix'}
                  </span>
                  <span className="text-[9px] md:text-[10px] font-normal text-gray-400 uppercase tracking-[0.2em] opacity-60 relative z-10">
                    Supports XLSX, XLS, or CSV formats
                  </span>
                </label>
              </div>

              <div>
                <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider mb-3 ml-1">Target Collective assignment</label>
                <div className="relative group">
                  <span className="material-icons-round absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-bulky-blue-500 transition-colors">hub</span>
                  <input
                    type="text"
                    list="groups"
                    value={formData.groupName}
                    onChange={(e) => setFormData({ ...formData, groupName: e.target.value })}
                    className="w-full pl-14 pr-6 py-4.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-800 shadow-sm"
                    placeholder="Collective designation for batch"
                    required
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-gray-100 mt-10">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full sm:flex-1 px-8 py-5 border-2 border-gray-100 text-bulky-navy-500 font-normal rounded-2xl hover:bg-gray-50 transition-all uppercase tracking-wider text-[10px]"
                >
                  Terminate
                </button>
                <button
                  type="submit"
                  disabled={!excelFile || importing}
                  className="w-full sm:flex-1 px-8 py-5 bg-bulky-navy-800 text-white font-normal rounded-2xl hover:bg-bulky-blue-600 shadow-xl shadow-bulky-blue-500/10 active:scale-95 transition-all text-[10px] uppercase tracking-[0.2em] disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                >
                  {importing ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <span className="material-icons-round text-lg">analytics</span>
                      Initialize Batch sequence
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ImportContactsModal;
